<template>
  <div
    class="install-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="install-modal-title"
  >
    <div class="install-modal__icon-ring" aria-hidden="true">
      <ion-icon :icon="mode !== 'prompt' ? shareOutline : downloadOutline" />
    </div>

    <h2 id="install-modal-title" class="install-modal__title">Install Syntry</h2>

    <!-- Chromium: the browser can do it for us. -->
    <template v-if="mode === 'prompt'">
      <p class="install-modal__message">
        Add Syntry to your home screen for quick access, a full-screen experience,
        and offline support.
      </p>
      <div class="install-modal__actions">
        <button
          type="button"
          class="install-modal__btn install-modal__btn--outline"
          :disabled="pending"
          @click="emit('cancel')"
        >
          Not now
        </button>
        <button
          type="button"
          class="install-modal__btn install-modal__btn--primary"
          :disabled="pending"
          @click="emit('install')"
        >
          <ion-spinner v-if="pending" name="crescent" class="install-modal__spinner" />
          <span v-else>Install</span>
        </button>
      </div>
    </template>

    <!-- Safari (iOS or macOS): no programmatic install exists, show the manual route. -->
    <template v-else>
      <p class="install-modal__message">{{ guideMessage }}</p>
      <ol class="install-modal__steps">
        <li v-for="(step, idx) in guideSteps" :key="idx" class="install-modal__step">
          <span class="install-modal__step-num" aria-hidden="true">{{ idx + 1 }}</span>
          <span>{{ step }}</span>
        </li>
      </ol>
      <div class="install-modal__actions">
        <button
          type="button"
          class="install-modal__btn install-modal__btn--primary install-modal__btn--full"
          @click="emit('cancel')"
        >
          Got it
        </button>
      </div>
    </template>

    <button
      v-if="showOptOut"
      type="button"
      class="install-modal__optout"
      :disabled="pending"
      @click="emit('optout')"
    >
      Don't ask me again
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * Install-this-app dialog. Stateless by design (same discipline as
 * SurveyPromptModal): it emits intents and never touches storage or the
 * install API — the host owns that.
 */
import { computed } from 'vue';
import { IonIcon, IonSpinner } from '@ionic/vue';
import { downloadOutline, shareOutline } from 'ionicons/icons';

const props = withDefaults(
  defineProps<{
    /** `prompt` when a native install prompt is available, else manual steps. */
    mode: 'prompt' | 'ios-guide' | 'mac-guide';
    /** Disables the buttons while the native prompt is open. */
    pending?: boolean;
    /** Offer the permanent "Don't ask me again" opt-out (daily promo only). */
    showOptOut?: boolean;
  }>(),
  { pending: false, showOptOut: false },
);

const emit = defineEmits<{
  (e: 'install'): void;
  (e: 'cancel'): void;
  (e: 'optout'): void;
}>();

const iosSteps: string[] = [
  "Tap the Share button in Safari's toolbar.",
  'Scroll down and tap "Add to Home Screen".',
  'Tap "Add" — Syntry will appear on your Home Screen.',
];

const macSteps: string[] = [
  "Click the Share button in Safari's toolbar.",
  'Choose "Add to Dock".',
  'Click "Add" — Syntry will appear in your Dock.',
];

const guideSteps = computed(() => (props.mode === 'mac-guide' ? macSteps : iosSteps));

const guideMessage = computed(() =>
  props.mode === 'mac-guide'
    ? 'Safari can add Syntry to your Dock in three steps.'
    : 'Safari can add Syntry to your Home Screen in three steps.',
);
</script>

<!-- Non-scoped: the Ionic modal renders in a portal, so the host styles must be
     global. Any page can reuse this via css-class="install-modal". -->
<style lang="scss">
ion-modal.install-modal {
  --background: transparent;
  --width: 88%;
  --max-width: 380px;
  --height: auto;
  --max-height: 90vh;
  --border-radius: var(--syntry-radius-modal);
  --box-shadow: none;
  --backdrop-opacity: 0.32;
  align-items: center;
  justify-content: center;
}

ion-modal.install-modal .ion-page {
  position: relative;
  contain: content;
  background: transparent;
  height: auto;
}
</style>

<style scoped lang="scss">
.install-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--sp-7) var(--sp-6) var(--sp-6);
  background: rgba(var(--syntry-surface-rgb), 0.82);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(var(--syntry-surface-rgb), 0.55);
  border-radius: var(--syntry-radius-modal);
  box-shadow: var(--syntry-shadow-elevated);

  &__icon-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 34px;
    margin-bottom: var(--sp-5);
    background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));
    color: var(--syntry-brand-soft-foreground, var(--syntry-primary));
  }

  &__title {
    font-size: var(--syntry-fs-h2);
    font-weight: 800;
    color: var(--syntry-text-strong);
    margin: 0 0 var(--sp-2);
    line-height: 1.3;
  }

  &__message {
    font-size: var(--syntry-fs-body);
    color: var(--syntry-text-muted);
    line-height: 1.5;
    margin: 0 0 var(--sp-6);
  }

  &__steps {
    list-style: none;
    margin: 0 0 var(--sp-6);
    padding: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }

  &__step {
    display: flex;
    align-items: flex-start;
    gap: var(--sp-3);
    text-align: left;
    font-size: var(--syntry-fs-small);
    color: var(--syntry-text);
    line-height: 1.45;
  }

  &__step-num {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));
    color: var(--syntry-brand-soft-foreground, var(--syntry-primary));
    font-size: var(--syntry-fs-tiny);
    font-weight: 700;
  }

  &__actions {
    display: flex;
    gap: var(--sp-3);
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    border-radius: var(--syntry-radius-button);
    font-size: var(--syntry-fs-body);
    font-weight: 700;
    font-family: var(--syntry-font);
    cursor: pointer;
    border: none;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 150ms ease;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:not(:disabled):active { opacity: 0.85; }

    &--outline {
      background: transparent;
      border: 2px solid var(--syntry-border);
      color: var(--syntry-text);
    }

    &--primary {
      background: var(--syntry-primary);
      color: #ffffff;
      box-shadow: var(--syntry-shadow-button);
    }

    &--full { flex: 1 1 100%; }
  }

  // Tertiary text action — deliberately muted (brand-first rules: no extra
  // colored buttons; this is an escape hatch, not a call to action).
  &__optout {
    margin-top: var(--sp-4);
    background: transparent;
    border: none;
    padding: var(--sp-1) var(--sp-2);
    font-size: var(--syntry-fs-small);
    font-family: var(--syntry-font);
    color: var(--syntry-text-muted);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:not(:disabled):active { text-decoration: underline; }
  }

  &__spinner {
    width: 22px;
    height: 22px;
    color: #ffffff;
  }
}
</style>
